/**
 * The campaign Pricing page — approved copy, 30 September 2026 (Q12).
 *
 * $99/month, replacing the $299 working assumption the PRD carried.
 *
 * The upgrade destination is deliberately deferred: Or's note says checkout
 * must not delay launch, that the continuation is decided later, and that no
 * embedded card form is approved. So the button is real and its destination is
 * not — `onUpgrade` is what a caller supplies when there is somewhere to go,
 * and the button is hidden rather than dead when there is not. That is the
 * same rule the guided-demo button follows on the report wait.
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { copyFor, isAudience, PRICING, SHARED } from './audienceCopy';
import CampaignShell from './CampaignShell';
import MockShell from './mockup/MockShell';
import { MockPricing } from './mockup/MockProse';
import { isMockup, resolveVariant } from './campaignVariant';
import './Campaign.css';

export default function CampaignPricingPage({ onRequestDemo, onUpgrade }) {
  const { audience } = useParams();
  const navigate = useNavigate();
  if (!isAudience(audience)) return null;
  // Sticky for the journey — see campaignVariant.
  const mock = isMockup(resolveVariant());
  const copy = copyFor(audience);
  // One form for every entry point (§4). Defaulted here so a page
  // rendered straight from a route still has somewhere to send people.
  const requestDemo = onRequestDemo || (() => navigate(`/c/${audience}/demo`));

  return mock ? (
    <MockShell audience={audience} rail={850} onRequestDemo={requestDemo}>
      <MockPricing audience={audience} onBack={() => navigate(`/c/${audience}`)} onRequestDemo={requestDemo} onUpgrade={onUpgrade} />
    </MockShell>
  ) : (

    <CampaignShell audience={audience} variant="prose" showResults onRequestDemo={requestDemo}>
      <p className="cmp-eyebrow">Pricing</p>
      <h1 className="cmp-title">{PRICING.title}</h1>
      <p className="cmp-lede">{copy.pricingIntro}</p>

      <div className="cmp-price-blocks">
        <section className="cmp-price-block">
          <p className="cmp-faq-answer">{PRICING.lede}</p>
        </section>

        <section className="cmp-price-block">
          <p className="cmp-price">{PRICING.price}</p>
          <p className="cmp-faq-answer">{PRICING.priceBody}</p>
          {/* Q12 — the destination is deliberately deferred, so the button is
              absent rather than dead until there is somewhere to send people. */}
          {onUpgrade && (
            <div className="cmp-actions">
              <button type="button" className="cmp-btn cmp-btn--primary" onClick={onUpgrade}>
                {SHARED.upgradeAction}
              </button>
            </div>
          )}
        </section>

        <section className="cmp-price-block">
          <h2 className="cmp-section-title">{PRICING.platformTitle}</h2>
          <p className="cmp-faq-answer">{PRICING.platformBody}</p>
          <div className="cmp-actions">
            {/* One filled orange primary per screen. With checkout deferred
                (Q12) the demo request IS this page's action and should look
                like it; when an upgrade destination exists it takes the
                primary and this steps back to the outlined style. */}
            <button
              type="button"
              className={`cmp-btn ${onUpgrade ? 'cmp-btn--secondary' : 'cmp-btn--primary'}`}
              onClick={requestDemo}
            >
              {SHARED.requestAGuidedDemo}
            </button>
          </div>
        </section>
      </div>
    </CampaignShell>
  );
}
